/*
  Загрузка данных - §3.3.2, §4.3.2.

  Экран первых минут работы: выбрали файл - увидели, как система его поняла,
  поправили и загрузили. Никаких предварительных настроек, справочников и
  подключений.

  Страница - контейнер: пять вкладок по видам файла и чек-лист готовности над
  ними. Разбор, состояние и отчёт по каждому виду файла живут в своём
  компоненте (`ChargesUpload`, `OneCUpload`, `StatementUpload`,
  `IdentityUpload`, `PremisesUpload`).

  🔴 Общего журнала загрузок внизу страницы больше нет (01.10.2026, указание
  владельца: «на каждом шаге должны быть файлы только этого шага, иначе
  определять, загрузили файл в шаг, нельзя»). Журнал переехал внутрь шага и
  вкладки и отбирается по виду файла - `StepJournal`. Одна лента на весь раздел
  отвечала на вопрос «что вообще грузили», а человеку в шаге нужен другой:
  грузил ли он файл СЮДА.

  🔴 Чек-лист добавлен 18.09.2026 по указанию владельца. До него экран отвечал
  на вопрос «что я загрузил» и не отвечал на главный - «хватает ли загруженного,
  чтобы подать»: узнать о пропуске можно было, только попробовав собрать пакет
  по одному счёту. Стоит он НАД вкладками, потому что относится ко всем сразу и
  отвечает на первый вопрос человека, открывшего раздел: что ещё от меня нужно.
*/

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api } from '../api/client.js'
import BankruptcyUpload from '../components/BankruptcyUpload.jsx'
import ChargesUpload from '../components/ChargesUpload.jsx'
import FilesTab from '../components/files/FilesTab.jsx'
import IdentityUpload from '../components/IdentityUpload.jsx'
import ImportSteps from '../components/ImportSteps.jsx'
import PremisesUpload from '../components/PremisesUpload.jsx'
import { Lock } from '../components/AccessContext.jsx'
import ReadinessChecklist from '../components/ReadinessChecklist.jsx'
import StatementUpload from '../components/StatementUpload.jsx'
import StepJournal from '../components/StepJournal.jsx'
import Tabs, { TabPanel } from '../components/Tabs.jsx'

/*
  Пять вкладок по видам файла: начисления - это долг, выписка - деньги,
  идентификаторы - чем подтверждается личность должника, сведения о помещении -
  чем наполняется финансово-лицевой счёт. Общего у них только механика
  («выбрали файл - увидели разбор - загрузили»), а дальше расходится всё, вплоть
  до того, что спорную строку выписки система оставляет человеку (§3.10.2).

  Отдельным разделом меню поступления не стали: §4.4 ставит пределом восемь
  разделов, а их уже десять.
*/
/*
  🔴 Порядок и группировка - макет владельца 30.09.2026.

  «Выгрузка 1С» стоит первой и открывается при входе: это рабочий путь заказчика,
  и внутри неё шаги идут по порядку (`ImportSteps`). Остальные виды файлов идут
  за ней - на сбор судебного пакета они не влияют и грузятся, когда понадобятся.

  До этого дня вкладок было шесть равноправных, «Начисления» первой. Порядок из
  такого ряда не читается: заказчик начинал с отчёта по начислениям, счетов в
  портфеле не возникало, и объяснять, с чего начинать, приходилось голосом.

  01.10.2026, решение владельца: подпись группы «по мере необходимости» перед
  остальными вкладками убрана. Она одна занимала в ряду около двухсот пикселей,
  и при окне 1024-1280 ряд выходил за край экрана (замер: 289 и 33 пикселя
  лишней ширины).
*/
/* Во вкладках журнал называется вкладкой, а не шагом: шаги живут только в ленте
   «Выгрузки 1С», и слово «шаг» здесь было бы неправдой. */
const TAB_FILES = 'Файлы, загруженные на эту вкладку'

const TABS = [
  { key: 'onec', title: 'Выгрузка 1С' },
  { key: 'charges', title: 'Начисления файлом' },
  { key: 'payments', title: 'Поступления' },
  { key: 'identities', title: 'Идентификаторы' },
  { key: 'premises', title: 'Помещения' },
  { key: 'bankruptcy', title: 'Банкроты' },
  // 30.09.2026, решение владельца: любые файлы в любом порядке, сведения
  // копятся по счёту. Последней в ряду - так решил владелец при выборе макета.
  { key: 'files', title: 'Файлы' },
]

export default function ImportPage() {
  const navigate = useNavigate()
  const [tab, setTab] = useState('onec')
  const [organisation, setOrganisation] = useState(null)

  /* Загрузка, строку которой журнал вкладки обязан дождаться. Сам журнал
     догоняет её сам (`StepJournal`): страница только называет ему номер. */
  const [loaded, setLoaded] = useState(null)

  /* Готовность данных грузится ЗДЕСЬ, а не внутри чек-листа (30.09.2026): её
     читают двое - чек-лист и лента шагов, - и два запроса одного и того же на
     одном экране дали бы два ответа, расходящихся на время загрузки файла. */
  const [readiness, setReadiness] = useState(null)
  const [readinessError, setReadinessError] = useState(null)

  const reloadReadiness = useCallback(() => {
    api
      .readiness()
      .then((result) => {
        setReadiness(result)
        // Удачное чтение снимает прошлую неудачу: иначе плашка висела бы над свежими данными.
        setReadinessError(null)
      })
      .catch(setReadinessError)
  }, [])

  useEffect(reloadReadiness, [reloadReadiness])

  /* Шёл ли перенос вкладки «Файлы», когда её видели в последний раз
     (01.10.2026, проверка перед выкладкой). Хранится здесь, а не во вкладке:
     вкладка разбирается при уходе на соседнюю, и перенос, кончившийся без
     неё, не обновлял чек-лист до перезагрузки страницы. Уход со страницы
     раздела память сбрасывает - но новая страница и так читает готовность
     заново. */
  const filesTransfer = useRef(false)

  /* Срок внесения платы живёт в карточке организации: он один на заказчика и
     берётся из договора управления. Отказ чтения не показываем ошибкой - право
     читать карточку есть не у всякого, кто грузит выгрузку, а экран загрузки
     без этой строки работает: сервер всё равно проверит и объяснит. */
  useEffect(() => {
    api
      .organisation()
      .then(setOrganisation)
      .catch(() => setOrganisation(null))
  }, [])

  /* Файл загружен: пересчитать готовность и назвать журналу номер загрузки,
     строку которой он обязан дождаться. Готовность человек для того и обновляет,
     что грузил файл, - чтобы строка чек-листа стала зелёной, а шаг в ленте
     залился. */
  const showInJournal = useCallback(
    (runId) => {
      reloadReadiness()
      setLoaded(runId ?? null)
    },
    [reloadReadiness],
  )

  return (
    <>
      <header className="head">
        <div>
          <h1 className="head__title">Загрузка данных</h1>
          <div className="head__note">
            Данные грузятся по порядку: сначала реестр должников, потом начисления по месяцам.
            Принимаем таблицу из учётной системы, CSV или XLSX: колонки Арбитр ЖКХ распознаёт
            сам, а вы проверяете и правите перед записью.
          </div>
        </div>
      </header>

      <ReadinessChecklist state={readiness} error={readinessError} />

      {/* Переход на другую вкладку снимает ожидание загрузки: её строки там нет
          и не будет, а журнал ждал бы её четырьмя лишними обращениями. */}
      <Tabs
        tabs={TABS}
        active={tab}
        onChange={(next) => {
          setLoaded(null)
          setTab(next)
        }}
      />

      {/* Внутри этой вкладки живёт весь рабочий путь заказчика: лента шагов и
          содержимое того шага, которым он сейчас занят. Сама вкладка о шагах не
          знает - иначе страница отвечала бы и за порядок загрузки, и за раздел
          целиком. */}
      <TabPanel tabKey="onec" active={tab}>
        <ImportSteps
          dueDay={organisation?.payment_due_day ?? null}
          readiness={readiness}
          onLoaded={showInJournal}
          onEditDueDay={() => navigate('/settings#payment-due-day')}
        />
      </TabPanel>

      {/* Начисления файлом и выгрузка 1С - один вид файла (`charges`), и журнал
          у них общий намеренно: оба пути заводят начисления, и человек, который
          вчера грузил отчёт, а сегодня файл, должен видеть обе загрузки. */}
      <TabPanel tabKey="charges" active={tab}>
        <Lock>
          <ChargesUpload
            dueDay={organisation?.payment_due_day ?? null}
            onLoaded={showInJournal}
            onEditDueDay={() => navigate('/settings#payment-due-day')}
          />
        </Lock>
        <StepJournal kind="charges" awaiting={loaded} title={TAB_FILES} />
      </TabPanel>

      <TabPanel tabKey="payments" active={tab}>
        <Lock>
          <StatementUpload onLoaded={showInJournal} />
        </Lock>
        <StepJournal kind="payments" awaiting={loaded} title={TAB_FILES} />
      </TabPanel>

      {/* Журнал загрузок эта вкладка не пополняет: она не заводит начисления, а
          правит сведения о людях, и отчёт о ней - разбор по строкам на месте. */}
      <TabPanel tabKey="identities" active={tab}>
        <Lock>
          <IdentityUpload onLoaded={reloadReadiness} />
        </Lock>
      </TabPanel>

      {/* Журнал загрузок эта вкладка не пополняет по той же причине, что
          идентификаторы: она не заводит начисления, а уточняет сведения о
          помещении, и отчёт о ней - разбор по строкам на месте. */}
      <TabPanel tabKey="premises" active={tab}>
        <Lock>
          <PremisesUpload onLoaded={reloadReadiness} />
        </Lock>
      </TabPanel>

      {/* Журнал загрузок эта вкладка не пополняет: она не заводит начислений, а
          останавливает взыскание по должникам, и отчёт о ней - разбор по
          строкам на месте. */}
      <TabPanel tabKey="bankruptcy" active={tab}>
        <Lock>
          <BankruptcyUpload onLoaded={reloadReadiness} />
        </Lock>
      </TabPanel>

      {/* Журнала у этой вкладки нет: присланные файлы она показывает своей
          таблицей. Перенос готовых счетов меняет портфель, поэтому после него
          пересчитывается чек-лист. */}
      <TabPanel tabKey="files" active={tab}>
        <FilesTab onTransferred={reloadReadiness} transferMemory={filesTransfer} />
      </TabPanel>
    </>
  )
}
